import React from 'react';
import { format } from 'date-fns';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ListBulletIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

export type CalendarView = 'month' | 'week' | 'day' | 'agenda';

interface CalendarToolbarProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  cursorDate: Date;
  onNavigate: (delta: -1 | 0 | 1) => void;
  onTodayClick: () => void;
  disabled?: boolean;
}

const VIEW_BUTTONS: Array<{
  view: CalendarView;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
}> = [
  { view: 'month', Icon: Squares2X2Icon, label: 'Month' },
  { view: 'week', Icon: ViewColumnsIcon, label: 'Week' },
  { view: 'day', Icon: CalendarDaysIcon, label: 'Day' },
  { view: 'agenda', Icon: ListBulletIcon, label: 'Agenda' },
];

const periodLabel = (view: CalendarView, cursor: Date): string => {
  switch (view) {
    case 'month':
      return format(cursor, 'MMMM yyyy');
    case 'week':
      return format(cursor, "'Week of' MMM d, yyyy");
    case 'day':
      return format(cursor, 'EEEE, MMM d, yyyy');
    case 'agenda':
      return format(cursor, 'MMMM yyyy');
    default:
      return '';
  }
};

const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  view,
  onViewChange,
  cursorDate,
  onNavigate,
  onTodayClick,
  disabled = false,
}) => {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-primary/15">
      {/* View switcher */}
      <div
        className="flex items-center gap-1 p-1 rounded-2 border border-border-primary/15 bg-surface-primary"
        role="tablist"
        aria-label="Calendar view"
      >
        {VIEW_BUTTONS.map(({ view: v, Icon, label }) => {
          const active = v === view;
          return (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={active}
              aria-pressed={active}
              aria-label={label}
              onClick={() => onViewChange(v)}
              disabled={disabled}
              className={[
                'w-8 h-8 flex items-center justify-center rounded-1.5',
                active
                  ? 'bg-primary-500/16 text-primary-500'
                  : 'text-text-secondary hover:text-text-primary hover:bg-action-hover',
                'disabled:opacity-50',
              ].join(' ')}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>

      {/* Period nav (centered) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onNavigate(-1)}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:text-text-primary hover:bg-action-hover disabled:opacity-50 active:scale-[0.95] transition-transform"
          aria-label="Previous"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h2 className="text-base font-semibold text-text-primary min-w-[180px] text-center select-none">
          {periodLabel(view, cursorDate)}
        </h2>
        <button
          type="button"
          onClick={() => onNavigate(1)}
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:text-text-primary hover:bg-action-hover disabled:opacity-50 active:scale-[0.95] transition-transform"
          aria-label="Next"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Today + filter */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onTodayClick}
          disabled={disabled}
          className="px-4 h-8 rounded-2 bg-primary-500/16 text-primary-500 text-sm font-bold hover:bg-primary-500/24 disabled:opacity-50 active:scale-[0.97] transition-transform"
        >
          Today
        </button>
        <button
          type="button"
          disabled={disabled}
          className="w-8 h-8 flex items-center justify-center rounded-full text-text-secondary hover:text-text-primary hover:bg-action-hover disabled:opacity-50"
          aria-label="Filter"
          title="Filter (coming soon)"
        >
          <AdjustmentsHorizontalIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default CalendarToolbar;
